import { autoserialize, autoserializeAs } from 'cerialize';
import * as moment from 'moment';

import { BaseDonationLead, DonationLeadType, IBaseDonationLead } from './base-donation-lead';
import { CallDetails, ICallDetails } from './call-details';
import { CourierType, isRoadie } from './courier-type';
import { Donation, IDonation } from './donation';
import { DonationMeta, IDonationMeta } from './donation-meta';
import { LeadChannel } from './lead-channel';
import { LeadUTM } from './lead-utm';

export class Lead extends BaseDonationLead implements ILead {
  @autoserializeAs('donation_id') donationId?: string;
  @autoserializeAs('donation') donation?: Donation;

  @autoserializeAs('call_details') callDetails?: CallDetails;

  @autoserializeAs('marketing_source') marketingSource?: string;
  @autoserialize utm?: LeadUTM;
  @autoserialize channel?: LeadChannel;

  @autoserialize meta?: DonationMeta;

  @autoserialize quote_check?: boolean | null;
  @autoserialize abandoned?: string | null;

  constructor(payload: ILead) {
    super(payload);

    this.type = DonationLeadType.LEAD;

    this.donation = payload?.donation ? new Donation(payload.donation) : undefined;
    this.donationId = payload?.donationId || payload?.donation?.id;

    this.callDetails = payload?.callDetails ? new CallDetails(payload.callDetails) : undefined;

    this.marketingSource = payload?.marketingSource;
    this.utm = payload?.utm ? new LeadUTM(payload.utm) : undefined;
    this.channel = payload?.channel;

    this.meta = new DonationMeta(payload?.meta);

    this.quote_check = payload?.quote_check;
    this.abandoned = payload?.abandoned;

    if (this.specification && this.pricing?.bookingFee && this.total === this.totalSponsoredAmount) {
      this.pricing.bookingFee = 0;
    }
  }

  get isExpired(): boolean {
    return moment().isAfter(moment(this.expiresAt));
  }

  get total(): number {
    return this.originalQuote;
  }

  get fuelFee(): number {
    return this.originalQuoteFuelFee;
  }

  get totalSponsoredAmount(): number {
    return this.originalSponsoredAmount;
  }

  get donatableSmall(): number {
    if (this.discount?.valid) {
      return this.specification.small;
    } else {
      return 0;
    }
  }

  /** Charity has Uber enabled. Falls back to the legacy `courier` boolean for charities
   *  whose `couriers` array hasn't been migrated yet (mirrors backend `Charity#uber_enabled?`). */
  private get charityUberEnabled(): boolean {
    return this.charity?.couriers ? this.charity.couriers.includes(CourierType.UBER) : !!this.charity?.courier;
  }

  /** Charity has Roadie enabled. No legacy fallback — pre-migration charities never had Roadie. */
  private get charityRoadieEnabled(): boolean {
    return !!this.charity?.couriers?.some(isRoadie);
  }

  /** True when the specification has only small items (medium/large/xlarge/staircases/disassembly/recycling all zero). */
  private get smallItemsOnly(): boolean {
    return Object.keys(this.specification).every((key) => {
      if (key === 'small') return true;
      return this.specification[key] === 0;
    });
  }

  private get hasRestrictedItem(): boolean {
    return !!this.items?.some((item) => item.restricted);
  }

  /** A courier fee is configured when either a flat fee or a percentage fee is set.
   *  Mirrors backend `Pricing#courier_fee_configured?` — percentage-based markets leave
   *  `courier_fee` nil but set `courierPercentFee`, and both must count as eligible. */
  private get courierFeeConfigured(): boolean {
    return this.pricing?.courier_fee > 0 || this.pricing?.courierPercentFee > 0;
  }

  get isUberEligible(): boolean {
    return (
      this.charityUberEnabled &&
      this.courierFeeConfigured &&
      this.specification.small >= 1 &&
      this.specification.small <= 5 &&
      this.smallItemsOnly &&
      !this.hasRestrictedItem
    );
  }

  get isRoadieEligible(): boolean {
    return (
      this.charityRoadieEnabled &&
      this.courierFeeConfigured &&
      this.specification.small >= 6 &&
      this.specification.small <= 10 &&
      this.smallItemsOnly &&
      !this.hasRestrictedItem
    );
  }

  get isCourierEligible(): boolean {
    return this.isUberEligible || this.isRoadieEligible;
  }

  get isLead(): boolean {
    return true;
  }

  get isDonation(): boolean {
    return false;
  }
}

export interface ILead extends IBaseDonationLead {
  donationId?: string;
  donation?: IDonation;

  callDetails?: ICallDetails;

  marketingSource?: string;
  utm?: LeadUTM;
  channel?: LeadChannel;

  meta?: IDonationMeta;

  quote_check?: boolean | null;
  abandoned?: string | null;
}
